/**
 * SSML Readme 「構文辞典」タブの静的データ (MVP の正源)。
 *
 * ## 本ファイルの位置づけ (Readme の 2 タブ構成)
 *
 * Readme は **「よく使われる事例」(初期表示) と「構文辞典」** の 2 タブで構成される。
 * 利用者の入口は「こんな読み方にしたい」という事例側 (`useCases.ts`) であり、
 * 本ファイルは **タグの意味を引くための辞典** として 2 番目のタブに置く。
 * 事例からは `relatedSyntax` (カテゴリ + エントリ title) で本ファイルを参照するため、
 * **エントリの `title` は事例側から参照される識別子** でもある。
 * 変更する場合は `useCases.ts` の `relatedSyntax` も併せて直す
 * (整合はテストで固定している)。
 *
 * `docs/ssml-tuning-plan.md` 8.1 項目 14 のとおり、MVP の Readme は
 * **DynamoDB / API を使わず本ファイルの定数だけ** で表示する。
 * カテゴリの **順序とラベルもこのファイルが正源** であり、二重管理は発生しない。
 *
 * ## 記載内容 (1 エントリあたり)
 *
 *   - `syntax`          … SSML 構文
 *   - `description`     … 説明 (何が起きるか)
 *   - `usages`          … 利用例 (どの放送で使うか)
 *   - `expectedEffect`  … 期待効果 (聞こえ方がどう変わるか)
 *   - `sampleSsml`      … そのまま試せる最小サンプル
 *
 * ## 収録した構文の範囲
 *
 * **Amazon Polly (ja-JP / 話者 Takumi / Neural エンジン) で実際に使える構文のみ**を載せる。
 * Neural エンジンでは非対応のタグ (`<emphasis>` / `<amazon:effect name="whispered">` など) が
 * あるため、非対応のものは載せず、同じ狙いを実現できる代替構文 (`<prosody>` の組み合わせ) を
 * 「強調」カテゴリに置いている。
 *
 * ⚠️ `<prosody pitch>` も Neural エンジンでは **非対応** (値に関係なく `InvalidSsmlException`)。
 * 2026-09-24 に実機で `pitch="+10%"` / `pitch="-5%"` (rate との併用を含む) がすべて失敗し、
 * pitch を外すと成功することを確認したため、pitch を使うエントリ・サンプルは載せない。
 * 使える `<prosody>` 属性は rate / volume のみ (`categories.test.ts` で検査している)。
 * 参考: Amazon Polly デベロッパーガイド「Supported SSML tags」の Neural 対応表。
 *
 * ## 更新の運用 (リスク R13)
 *
 * 内容の追加・修正は **PR で行う** (手編集 UI や AI 自動育成は Phase2 送り)。
 * 実運用との乖離を検知できるよう `SSML_README_LAST_UPDATED` を必ず更新する。
 */
import type { SsmlReadmeCategory } from './types';

/**
 * Readme の最終更新日 (ISO8601 の日付)。
 *
 * 静的データが実運用と乖離していないかを利用者が判断するための情報 (計画 R13)。
 * `categories.ts` の内容を変更したら必ず更新する。
 */
export const SSML_README_LAST_UPDATED = '2026-09-24';

/**
 * 6 カテゴリの Readme データ。
 *
 * 配列の順序がタブの並び順になる (強調 → 間 → 再生速度 → イントネーション → 音量 → その他)。
 */
export const SSML_README_CATEGORIES: readonly SsmlReadmeCategory[] = [
  {
    category: 'emphasis',
    label: '強調',
    description:
      '聞き取ってほしい語 (駅名・行先・遅延時分) を目立たせる構文。Neural エンジンでは <emphasis> が使えないため、話速・音量・音高を組み合わせて強調を作る。',
    entries: [
      {
        title: '駅名をゆっくり大きく読んで強調する',
        syntax: '<prosody rate="90%" volume="+2dB">…</prosody>',
        description:
          '囲んだ範囲だけ話速を落としつつ音量を上げる。Neural エンジンで <emphasis level="strong"> の代わりに使う定番の組み合わせ。',
        usages: [
          '「次は◯◯です」の駅名を確実に聞き取らせたいとき',
          '行先や乗り換え案内の路線名を目立たせたいとき',
        ],
        expectedEffect:
          '該当語の前後と速度差・音量差が生まれ、周囲の走行音の中でも駅名が浮き上がって聞こえる。',
        sampleSsml: '<speak>次は<prosody rate="90%" volume="+2dB">梅田</prosody>です。</speak>',
      },
      {
        title: '遅延時分など数値を強調する',
        syntax: '<prosody rate="85%" volume="+2dB">…</prosody>',
        description:
          '話速を落としつつ音量をわずかに上げる。数字は聞き逃されやすいため、速度と音量の両方で差を付ける (Neural エンジンは pitch 非対応のため音高は使わない)。',
        usages: [
          '「約◯分の遅れ」の分数を聞き取らせたいとき',
          '発車時刻・番線などの数値を伝えるとき',
        ],
        expectedEffect:
          '数値部分がはっきり分離して聞こえ、「何分遅れなのか」を聞き返されにくくなる。',
        sampleSsml:
          '<speak>ただ今、<prosody rate="85%" volume="+2dB">およそ3分</prosody>の遅れで運転しています。</speak>',
      },
      {
        title: '数字・記号の読み方を指定して誤読を防ぐ',
        syntax: '<say-as interpret-as="cardinal">…</say-as>',
        description:
          '囲んだ文字列の解釈方法を指定する。基数 (cardinal) / 序数 (ordinal) / 数字読み (digits) / 文字読み (characters) などを指定できる。',
        usages: [
          '「3番線」を「さんばんせん」と読ませたいとき',
          '番号を 1 桁ずつ読ませたいとき (digits)',
        ],
        expectedEffect:
          '数値の読み違い (「3」を「みっつ」と読むなど) がなくなり、案内の意味が正しく伝わる。',
        sampleSsml:
          '<speak><say-as interpret-as="cardinal">3</say-as>番線に電車が到着します。</speak>',
      },
      {
        title: '固有名詞の読みをふりがなで指定する',
        syntax: '<sub alias="よみがな">表記</sub>',
        description: '表記を読み替える。難読の駅名・社名を意図した読みで発話させる。',
        usages: [
          '難読駅名 (放出・喜連瓜破など) を正しく読ませたいとき',
          '略称を正式名称で読ませたいとき',
        ],
        expectedEffect: '固有名詞が確実に正しい読みで発話され、利用者が地名を誤解しない。',
        sampleSsml: '<speak>次は<sub alias="はなてん">放出</sub>です。</speak>',
      },
    ],
  },
  {
    category: 'break',
    label: '間',
    description:
      '文の区切りに沈黙を挿入して聞き取りやすさを整える構文。間が無いと情報が続けて流れ、走行音の中では聞き分けが難しくなる。',
    entries: [
      {
        title: '文の区切りに短い間を入れる',
        syntax: '<break time="300ms"/>',
        description:
          '指定した長さの沈黙を挿入する (ms / s 単位、最大 10 秒)。閉じタグを持たない空要素。',
        usages: [
          '「次は◯◯です」と「お出口は右側です」の間に一拍置きたいとき',
          '前置き (お知らせ) と本文を分けたいとき',
        ],
        expectedEffect: '情報の切れ目がはっきりし、続く文の頭が埋もれず聞き取れる。',
        sampleSsml: '<speak>次は梅田です。<break time="300ms"/>お出口は右側です。</speak>',
      },
      {
        title: '重要な案内の前で長めに間を取る',
        syntax: '<break time="700ms"/>',
        description: '0.5〜1 秒程度の長い沈黙を挿入する。次に来る情報へ注意を向けさせる。',
        usages: [
          'お詫びや注意喚起の直前で聞く姿勢を作りたいとき',
          '乗り換え案内に入る前に区切りを付けたいとき',
        ],
        expectedEffect: '沈黙で注意が引き戻され、直後の重要情報が印象に残る。',
        sampleSsml:
          '<speak>お客様にお知らせいたします。<break time="700ms"/>ただ今、電車が遅れております。</speak>',
      },
      {
        title: '間の強さを段階で指定する',
        syntax: '<break strength="strong"/>',
        description:
          '時間ではなく強さで間を指定する (none / x-weak / weak / medium / strong / x-strong)。文章構造に応じた自然な間になる。',
        usages: ['ミリ秒で細かく決めたくないとき', '読点・句点相当の自然な間にしたいとき'],
        expectedEffect: '句読点に近い自然な間になり、機械的に区切った印象を与えにくい。',
        sampleSsml:
          '<speak>ご案内いたします。<break strength="strong"/>この電車は普通電車です。</speak>',
      },
      {
        title: '文単位・段落単位で自動的に間を作る',
        syntax: '<s>…</s> / <p>…</p>',
        description:
          '文 (<s>) や段落 (<p>) として囲むと、その境界に自動で間が入る。<break> を都度書かずに構造で間を作れる。',
        usages: ['複数の文をまとめて読ませるとき', '案内文が長く、段落ごとに区切りたいとき'],
        expectedEffect: '文ごとに一定の間が入り、全体のリズムが揃って聞きやすくなる。',
        sampleSsml: '<speak><p><s>次は梅田です。</s><s>お出口は右側です。</s></p></speak>',
      },
    ],
  },
  {
    category: 'rate',
    label: '再生速度',
    description:
      '話速を調整する構文。全体をゆっくりにして聞き取りやすくしたり、定型の前置きだけ速めて本題に時間を割いたりできる。',
    entries: [
      {
        title: '全体をゆっくり読ませる',
        syntax: '<prosody rate="90%">…</prosody>',
        description:
          '囲んだ範囲の話速を百分率で指定する (100% が標準)。90% 前後が自然に聞こえる下限の目安。',
        usages: ['高齢の利用者が多い時間帯・区間の案内', '駅名や乗り換えなど情報量の多い案内'],
        expectedEffect: '一語一語が明瞭になり、走行音がある車内でも聞き取りやすくなる。',
        sampleSsml: '<speak><prosody rate="90%">次は梅田です。お出口は右側です。</prosody></speak>',
      },
      {
        title: '定型の前置きだけ速く読ませる',
        syntax: '<prosody rate="110%">…</prosody>',
        description: '話速を上げる。毎回同じ前置きは速めても情報が失われにくい。',
        usages: ['「お客様にお知らせいたします」などの定型句', '放送全体を短く収めたいとき'],
        expectedEffect:
          '放送全体の尺が縮み、重要情報に時間を割り当てられる。前置きが冗長に感じられにくい。',
        sampleSsml:
          '<speak><prosody rate="110%">お客様にお知らせいたします。</prosody>この電車は3分ほど遅れています。</speak>',
      },
      {
        title: '段階指定 (slow / fast) で話速を変える',
        syntax: '<prosody rate="slow">…</prosody>',
        description:
          'x-slow / slow / medium / fast / x-fast の段階で指定する。百分率を決めきれないときの簡易指定。',
        usages: ['まず大まかに速度感を試したいとき', 'AI 生成の SSML を手早く調整したいとき'],
        expectedEffect: '数値調整なしで速度差を確認でき、狙いに近い段階から百分率へ詰めていける。',
        sampleSsml: '<speak><prosody rate="slow">足元にご注意ください。</prosody></speak>',
      },
      {
        title: '重要語だけ速度を落として目立たせる',
        syntax: '<prosody rate="85%">…</prosody>',
        description:
          '文全体は標準速度のまま、特定の語のみ速度を落とす。強調カテゴリの手法と同じ考え方。',
        usages: [
          '行先・種別 (「快速」「普通」) を明確に伝えたいとき',
          '駅名だけを確実に聞かせたいとき',
        ],
        expectedEffect: '速度差によるコントラストが生まれ、囲んだ語が際立って聞こえる。',
        sampleSsml: '<speak>この電車は<prosody rate="85%">快速</prosody>、天王寺行きです。</speak>',
      },
    ],
  },
  {
    category: 'intonation',
    label: 'イントネーション',
    description:
      '読み (アクセント) と、速度・音量の組み合わせで抑揚や印象を整える構文。⚠️ Neural エンジン (話者 Takumi) は <prosody pitch> に対応しておらず、指定すると合成が失敗するため、音の高さは直接変えない。',
    entries: [
      {
        title: 'カナ読みでアクセントを指定して抑揚を試す',
        syntax: '<phoneme alphabet="x-amazon-pron-kana" ph="ナカ\'モズ">…</phoneme>',
        description:
          "読みをカタカナで直接指定する。x-amazon-pron-kana ではアクセント核をアポストロフィ (') で示せるため、地名のアクセントを狙った位置に置くことを試せる。",
        usages: [
          '「◯◯方面」の地名アクセントが平板になるとき',
          '駅名・線区名のイントネーションが不自然なとき',
        ],
        expectedEffect:
          'アクセント核の位置が狙いに近づき、地名として聞き取りやすくなる可能性がある。⚠️ 語や話者更新によって ph 指定が反映されない場合もあるため、確実な改善手段ではなく「試して確かめる方法」として使う。',
        sampleSsml:
          '<speak><phoneme alphabet="x-amazon-pron-kana" ph="ナカ\'モズ">なかもず</phoneme>方面ゆきです。</speak>',
      },
      {
        title: '速度と音量をまとめて指定して印象を整える',
        syntax: '<prosody rate="95%" volume="-1dB">…</prosody>',
        description:
          '<prosody> は rate と volume を同時指定できる。少しゆっくり・控えめな音量にすると落ち着いた印象になる。Neural エンジンは pitch 非対応のため、声の印象は速度・音量・間 (<break>) の組み合わせで作る。',
        usages: [
          '「申し訳ない感じでゆっくり」など複合的な要望',
          '1 つの候補で狙った雰囲気を作り込みたいとき',
        ],
        expectedEffect: '速度と音量が一貫し、狙った雰囲気 (丁寧・落ち着き) にまとまって聞こえる。',
        sampleSsml:
          '<speak><prosody rate="95%" volume="-1dB">ただ今、電車が遅れております。</prosody></speak>',
      },
    ],
  },
  {
    category: 'volume',
    label: '音量',
    description:
      '音の大きさを調整する構文。走行音のある車内で埋もれないようにしたり、前置きを控えめにして本文を相対的に目立たせたりできる。',
    entries: [
      {
        title: '重要な案内の音量を上げる',
        syntax: '<prosody volume="+3dB">…</prosody>',
        description:
          '音量を dB で相対指定する。+6dB を超えると割れて聞こえやすいため +3dB 程度までが実用的。',
        usages: ['駅名・行先を確実に届けたいとき', '走行音が大きい区間の案内'],
        expectedEffect:
          '騒音に埋もれず届くようになる。上げすぎると音割れするため段階的に確認する。',
        sampleSsml: '<speak>次は<prosody volume="+3dB">梅田</prosody>です。</speak>',
      },
      {
        title: '前置きの音量を下げて本文を目立たせる',
        syntax: '<prosody volume="-2dB">…</prosody>',
        description: '音量を下げる。全体を上げるのではなく前置きを下げることで、本文との差を作る。',
        usages: ['定型の前置きを控えめにしたいとき', '注意書き・補足を副次的に聞かせたいとき'],
        expectedEffect: '本文との音量差で本文側が相対的に際立ち、放送全体がうるさくならない。',
        sampleSsml:
          '<speak><prosody volume="-2dB">お客様にお知らせいたします。</prosody>この電車は3分遅れています。</speak>',
      },
      {
        title: '段階指定 (soft / loud) で音量を変える',
        syntax: '<prosody volume="loud">…</prosody>',
        description:
          'silent / x-soft / soft / medium / loud / x-loud の段階で指定する。dB を決めきれないときに使う。',
        usages: [
          'まず大まかな音量差を確認したいとき',
          '端末側の音量設定に依存せず相対差を付けたいとき',
        ],
        expectedEffect: 'dB 調整なしで音量差を確認でき、狙いに近い段階から dB へ詰めていける。',
        sampleSsml:
          '<speak><prosody volume="loud">ドアが閉まります。ご注意ください。</prosody></speak>',
      },
      {
        title: '音量と速度を合わせて聞き取りやすくする',
        syntax: '<prosody volume="+2dB" rate="95%">…</prosody>',
        description: '音量だけ上げると声が硬く聞こえるため、わずかに速度を落として明瞭さを補う。',
        usages: ['騒音下でも確実に伝えたい注意喚起', '高齢者向けに聞き取りやすさを優先したいとき'],
        expectedEffect: '大きさと明瞭さが両立し、音量だけ上げた場合より聞き疲れしにくい。',
        sampleSsml:
          '<speak><prosody volume="+2dB" rate="95%">足元にご注意ください。</prosody></speak>',
      },
    ],
  },
  {
    category: 'other',
    label: 'その他',
    description:
      '上記カテゴリに当てはまらない構文と、SSML を書くうえでの共通ルール。SSML の必須構造や、Neural エンジンで使えないタグの注意もここに置く。',
    entries: [
      {
        title: 'SSML 全体を <speak> で囲む (必須)',
        syntax: '<speak>…</speak>',
        description:
          'SSML のルート要素。これが無い文字列を Amazon Polly に渡すと構文エラー (InvalidSsmlException) になり、音声が作られない。',
        usages: [
          'SSML を手で編集したあとに囲み忘れがないか確認するとき',
          'AI が生成した SSML をそのまま使うとき',
        ],
        expectedEffect:
          '合成が成功する。囲み忘れの場合はエラー表示になるため、まずここを確認する。',
        sampleSsml: '<speak>この電車は普通電車、天王寺行きです。</speak>',
      },
      {
        title: '記号や特殊文字をエスケープする',
        syntax: '&amp; / &lt; / &gt;',
        description:
          'SSML は XML なので、本文中の & < > はそのまま書けない。実体参照へ置き換える必要がある。',
        usages: [
          '文中に「&」を含む名称を読ませたいとき',
          '編集後に合成が 400 で失敗する原因を切り分けたいとき',
        ],
        expectedEffect:
          '構文エラーを避けられる。エスケープ漏れは合成失敗の典型的な原因なので最初に疑う。',
        sampleSsml: '<speak>お問い合わせは、駅係員 &amp; 乗務員までお願いします。</speak>',
      },
      {
        title: '日付・時刻の読み方を指定する',
        syntax: '<say-as interpret-as="time">…</say-as>',
        description:
          'say-as の interpret-as に time / date などを指定し、時刻や日付として読ませる。',
        usages: [
          '発車時刻を「じゅうななじさんじゅっぷん」と読ませたいとき',
          '日付を含む運行情報の案内',
        ],
        expectedEffect: '数字の羅列として読まれず、時刻・日付として自然に発話される。',
        sampleSsml: '<speak>次の発車は<say-as interpret-as="time">17:30</say-as>です。</speak>',
      },
      {
        title: 'Neural エンジンで使えないタグに注意する',
        syntax: '<emphasis> / <amazon:effect name="whispered"> / <prosody pitch> ほか',
        description:
          '本システムは Polly の Neural エンジン (話者 Takumi) 固定で、これらのタグ・属性は非対応。使用すると無視されるか合成が失敗する。特に <prosody pitch> は値 (+10% / -5% / low など) に関係なく合成が失敗する (2026-09-24 実機確認)。',
        usages: [
          '他システム由来の SSML を持ち込むとき',
          '強調したいが <emphasis> が効かないとき (代わりに「強調」カテゴリの prosody を使う)',
          '声の高さを変えたいとき (pitch は使えないため、速度・音量・間の組み合わせで代替する)',
        ],
        expectedEffect:
          '非対応タグを避けることで「書いたのに効かない」「合成が失敗する」を防げる。強調は prosody の rate / volume で代替する。',
        sampleSsml: '<speak>次は<prosody rate="90%" volume="+2dB">梅田</prosody>です。</speak>',
      },
    ],
  },
];

/**
 * 既定で開くカテゴリ (先頭 = 強調)。
 *
 * 画面側で「先頭要素」を毎回導出しないよう、正源側で明示する。
 */
export const DEFAULT_SSML_README_CATEGORY = SSML_README_CATEGORIES[0].category;

/**
 * カテゴリキーから 1 カテゴリを取得する。
 *
 * 未知のキー (URL 直打ちなどで壊れた値が来た場合) では `undefined` を返すため、
 * 呼び出し側で既定カテゴリへフォールバックできる。
 */
export function findSsmlReadmeCategory(
  category: string,
): (typeof SSML_README_CATEGORIES)[number] | undefined {
  return SSML_README_CATEGORIES.find((c) => c.category === category);
}
